import React, { useState, useEffect, useContext } from 'react';
import { styled } from 'linaria/react';
import { useLazyQuery, useQuery } from 'react-apollo';
import { WarehouseStockQuery } from '../WarehouseStockQuery.gql';
import storeLocatorQuery from '../../StoreLocator/StoreLocator.gql';
import { ReactComponent as StoreFinderIcon } from '../../../svg/StoreFinderIcon.svg';
import { ReactComponent as Cross } from '../../../svg/Cross.svg';
import Drawer, { DrawerTarget, DrawerTrigger } from '@jetshop/ui/Modal/Drawer';
import slugify from 'slugify';
import t, { useIntl } from '@jetshop/intl';
import { theme } from '../../Theme';
import { Link } from 'react-router-dom';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import { useFavoriteStore } from '../../../contexts/FavoriteStoreContext';

const StoreStockWrapper = styled.div`
  width: 100%;
  height: 100%;
  border: 1px solid #d2d2d2;
  border-radius: 6px;
  padding: 16px 24px;

  h3 {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    font-weight: 600;
    color: #000;
    margin-bottom: 10px;
  }

  svg {
    width: 20px;
    height: 20px;
    color: #000;
  }
`;

const StockInfo = styled.div`
  margin-top: 4px;
  font-size: 14px;
`;

const FavoriteStoreInfo = styled.div`
  margin-top: 4px;
  font-size: 14px;

  a {
    color: #000;
    text-decoration: underline;
    font-size: 14px;
    cursor: pointer;
  }

  .in-stock {
    color: #235837;
  }

  .not-in-stock {
    color: #ef7622;
  }
`;

const ShowStoresButton = styled.button`
  background: none;
  border: none;
  color: #000;
  text-decoration: underline;
  font-size: 14px;
  margin-left: 5px;
  cursor: pointer;
  padding: 0;
`;

const DrawerContent = styled.div`
  width: 100%;
  height: 100%;
  padding: 20px;
  background-color: white;
  overflow-y: auto;
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;

  h3 {
    font-size: 18px;
    margin: 0;
    font-weight: 600;
  }

  svg {
    cursor: pointer;
    width: 16px;
    height: 16px;
  }
`;

const WarehouseList = styled.div`
  display: block;
`;

const WarehouseItem = styled.div`
  padding: 12px 0 12px 30px;
  position: relative;
  border-bottom: 1px solid #d2d2d2;

  &.no-variant-selected {
    padding: 12px 0;
  }

  .store-info {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .store-status {
    min-width: 100px;
    width: 100%;
    font-size: 14px;
    text-align: right;
    font-weight: normal;
    align-self: center;
  }

  &.in-stock {
    &:after {
      background: #419241;
    }
  }

  &.not-in-stock {
    &:after {
      background: #ca1313;
    }
  }
`;

const StoreName = styled.div`
  font-weight: bold;
  font-size: 16px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const StoreLink = styled(Link)`
  background: ${props => (props.primary ? '#000' : 'white')};
  color: ${props => (props.primary ? '#fff' : '#333')};
  border: ${props => (props.primary ? 'none' : '1px solid #333')};
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    background: ${props => (props.primary ? '#333' : '#f5f5f5')};
  }
`;

const StoreButton = styled('button')`
  background: ${props => (props.primary ? '#000' : 'white')};
  color: ${props => (props.primary ? '#fff' : '#333')};
  border: ${props => (props.primary ? 'none' : '1px solid #333')};
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    background: ${props => (props.primary ? '#333' : '#f5f5f5')};
  }
`;

const ChangeStoreButton = styled.button`
  background: none;
  border: none;
  color: #000;
  text-decoration: underline;
  font-size: 14px;
  margin-left: 5px;
  cursor: pointer;
  padding: 0;
`;

const StockCategory = styled.div`
  margin: 15px 0 5px;
  font-weight: 600;
  font-size: 16px;
  color: #000;
  border-bottom: 1px solid #eee;
  padding-bottom: 5px;
`;

const DrawerFooter = styled.div`
  margin-top: 20px;
  padding-top: 10px;
  font-size: 12px;
  color: #666;
`;

const DrawerWrapper = styled.div`
  & > div {
    margin-top: 100px;
    height: calc(100% - 100px);
    z-index: 9999;
    ${theme.below.xl} {
      margin-top: 116px;
      height: calc(100% - 116px);
    }
    ${theme.below.lg} {
      margin-top: 92px;
      height: calc(100% - 92px);
    }
    ${theme.below.md} {
      margin-top: 136px;
      height: calc(100% - 136px);
    }
    ${theme.below.sm} {
      margin-top: 60px;
      height: calc(100% - 60px);
    }
  }
`;

// Store selector for when no variant is selected
const SimpleStoreSelector = ({
  favoriteStore,
  onSelectFavorite,
  hideTarget,
  allStores
}) => {
  const sortedStores = [...allStores].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const translate = useIntl();
  const readMoreString = translate('Read more about');
  const selectString = translate('Select');
  const asFavoriteString = translate('as favorite store');

  return (
    <>
      {favoriteStore && (
        <>
          <StockCategory>{t('Favorite store')}</StockCategory>
          <WarehouseItem className="no-variant-selected favorite-store">
            <div className="store-info">
              <StoreName>{favoriteStore}</StoreName>
              <ButtonContainer>
                <StoreLink
                  to={'/stores/' + slugify(favoriteStore).toLowerCase()}
                >
                  {t('Read more')}
                </StoreLink>
              </ButtonContainer>
            </div>
          </WarehouseItem>
        </>
      )}

      <StockCategory>{t('All stores')}</StockCategory>
      {sortedStores
        .filter(store => store.name !== favoriteStore)
        .map(store => (
          <WarehouseItem key={store.id} className="no-variant-selected">
            <div className="store-info">
              <StoreName>{store.name}</StoreName>
              <ButtonContainer>
                <StoreLink
                  to={'/stores/' + slugify(store.name).toLowerCase()}
                  aria-label={`${readMoreString} ${store.name}`}
                >
                  {t('Read more')}
                </StoreLink>
                <StoreButton
                  aria-label={`${selectString} ${store.name} ${asFavoriteString}`}
                  primary={'true'}
                  onClick={() => {
                    onSelectFavorite(store.name);
                    if (hideTarget) hideTarget();
                  }}
                >
                  {t('Choose')}
                </StoreButton>
              </ButtonContainer>
            </div>
            <div className="store-status"></div>
          </WarehouseItem>
        ))}
    </>
  );
};

// Main warehouse component showing stock status
const Warehouse = ({
  warehouseStock,
  favoriteStore,
  onSelectFavorite,
  hideTarget,
  allStores
}) => {
  // Merge warehouse stock data with all stores
  const enrichedStores = allStores.map(store => {
    const stockData = warehouseStock.find(
      stock => stock.location && stock.location.name === store.name
    );

    return {
      ...store,
      stockLevel: stockData ? stockData.stockLevel : 0,
      hasStock: stockData ? stockData.stockLevel > 0 : false,
      inAssortment: !!stockData
    };
  });

  const favoriteStoreData = favoriteStore
    ? enrichedStores.find(store => store.name === favoriteStore)
    : null;

  const filteredStores = favoriteStore
    ? enrichedStores.filter(store => store.name !== favoriteStore)
    : enrichedStores;

  // Group stores by stock status
  const inStockStores = filteredStores.filter(store => store.hasStock);
  const outOfStockStores = filteredStores.filter(
    store => store.inAssortment && !store.hasStock
  );
  const notInAssortmentStores = filteredStores.filter(
    store => !store.inAssortment
  );

  const sortStoresByName = stores => {
    return stores.sort((a, b) => a.name.localeCompare(b.name));
  };

  const sortedInStockStores = sortStoresByName(inStockStores);
  const sortedOutOfStockStores = sortStoresByName(outOfStockStores);
  const sortedNotInAssortmentStores = sortStoresByName(notInAssortmentStores);

  return (
    <>
      {/* Favorite store section */}
      {favoriteStore && favoriteStoreData && (
        <>
          <StockCategory>{t('Selected store')}</StockCategory>
          <WarehouseItem
            className={favoriteStoreData.hasStock ? 'in-stock' : 'not-in-stock'}
          >
            <div className="store-info">
              <StoreName>{favoriteStore}</StoreName>
              <ButtonContainer>
                <StoreLink
                  to={'/stores/' + slugify(favoriteStore).toLowerCase()}
                >
                  {t('Läs mer')}
                </StoreLink>
              </ButtonContainer>
            </div>
            <div className="store-status">
              {favoriteStoreData.inAssortment
                ? favoriteStoreData.hasStock
                  ? t('in stock')
                  : t('not in stock')
                : t('not in stock')}
            </div>
          </WarehouseItem>
        </>
      )}

      {/* Stores with stock */}
      {sortedInStockStores.length > 0 && (
        <>
          <StockCategory>{t('Stores with stock')}</StockCategory>
          {sortedInStockStores.map(store => (
            <WarehouseItem
              key={`in-stock-${store.id}`}
              className={`in-stock ${
                store.name === favoriteStore ? 'selected' : ''
              }`}
            >
              <div className="store-info">
                <StoreName>
                  {store.name}
                  {store.name === favoriteStore && (
                    <span
                      style={{
                        marginLeft: '8px',
                        fontSize: '12px',
                        color: '#419241',
                        fontWeight: 'normal'
                      }}
                    >
                      ✓ {t('Selected')}
                    </span>
                  )}
                </StoreName>
                <ButtonContainer>
                  <StoreLink
                    to={'/stores/' + slugify(store.name).toLowerCase()}
                  >
                    {t('Read more')}
                  </StoreLink>
                  {store.name !== favoriteStore && (
                    <StoreButton
                      primary={'true'}
                      onClick={() => {
                        onSelectFavorite(store.name);
                        if (hideTarget) hideTarget();
                      }}
                    >
                      {t('Choose')}
                    </StoreButton>
                  )}
                </ButtonContainer>
              </div>
              <div className="store-status">{t('in stock')}</div>
            </WarehouseItem>
          ))}
        </>
      )}

      {/* Stores without stock */}
      {sortedOutOfStockStores.length > 0 && (
        <>
          <StockCategory>{t('stores without stock')}</StockCategory>
          {sortedOutOfStockStores.map(store => (
            <WarehouseItem
              key={`out-of-stock-${store.id}`}
              className="not-in-stock"
            >
              <div className="store-info">
                <StoreName>{store.name}</StoreName>
                <ButtonContainer>
                  <StoreLink
                    to={'/stores/' + slugify(store.name).toLowerCase()}
                  >
                    {t('Read more')}
                  </StoreLink>
                  {store.name !== favoriteStore && (
                    <StoreButton
                      primary={'true'}
                      onClick={() => {
                        onSelectFavorite(store.name);
                        if (hideTarget) hideTarget();
                      }}
                    >
                      {t('Choose')}
                    </StoreButton>
                  )}
                </ButtonContainer>
              </div>
              <div className="store-status">{t('not in stock')}</div>
            </WarehouseItem>
          ))}
        </>
      )}

      {/* Stores not in assortment */}
      {sortedNotInAssortmentStores.length > 0 && (
        <>
          {/* <StockCategory>Butiker utan produkten i sortimentet</StockCategory> */}
          {sortedNotInAssortmentStores.map(store => (
            <WarehouseItem
              key={`not-in-assortment-${store.id}`}
              className="not-in-stock"
            >
              <div className="store-info">
                <StoreName>{store.name}</StoreName>
                <ButtonContainer>
                  <StoreLink
                    to={'/stores/' + slugify(store.name).toLowerCase()}
                  >
                    {t('Read more')}
                  </StoreLink>
                  {store.name !== favoriteStore && (
                    <StoreButton
                      primary={'true'}
                      onClick={() => {
                        onSelectFavorite(store.name);
                        if (hideTarget) hideTarget();
                      }}
                    >
                      {t('Choose')}
                    </StoreButton>
                  )}
                </ButtonContainer>
              </div>
              <div className="store-status">{t('not in stock')}</div>
            </WarehouseItem>
          ))}
        </>
      )}
    </>
  );
};

// Drawer component
const StoreStockDrawer = ({
  isOpen,
  hideTarget,
  warehouseStock,
  favoriteStore,
  onSelectFavorite,
  needsVariantSelection,
  hasSelectedVariant,
  allStores
}) => {
  const shouldShowStockStatus = !needsVariantSelection || hasSelectedVariant;

  return (
    <DrawerContent>
      <DrawerHeader>
        <h3>
          {shouldShowStockStatus
            ? t('Stockstatus in store')
            : t('Select favorite store')}
        </h3>
        <Cross onClick={hideTarget} />
      </DrawerHeader>
      <WarehouseList>
        {shouldShowStockStatus ? (
          <Warehouse
            warehouseStock={warehouseStock}
            favoriteStore={favoriteStore}
            onSelectFavorite={onSelectFavorite}
            hideTarget={hideTarget}
            allStores={allStores}
          />
        ) : (
          <SimpleStoreSelector
            favoriteStore={favoriteStore}
            onSelectFavorite={onSelectFavorite}
            hideTarget={hideTarget}
            allStores={allStores}
          />
        )}
      </WarehouseList>
      {shouldShowStockStatus && (
        <DrawerFooter>
          {t(
            'Stock availability may change, please contact store to ensure availability.'
          )}
        </DrawerFooter>
      )}
    </DrawerContent>
  );
};

// Main component
export const StoreStock = ({ product, initialIndex, voyadoFavoriteStore }) => {
  const [selectedVariant, setSelectedVariant] = useState(null);
  const { favoriteStore, setFavoriteStore, getAllStores } = useFavoriteStore();
  const { selectedChannel } = useContext(ChannelContext);

  const { data: storesData, loading: storesLoading } =
    useQuery(storeLocatorQuery);

  const [getWarehouseStock, { loading, data }] = useLazyQuery(
    WarehouseStockQuery,
    {
      variables: {
        articleNumber: product?.articleNumber
      }
    }
  );

  // Check if we have variants and if a variant needs to be selected
  const hasVariants = data?.product?.variants?.options?.length > 0;
  const neededVariants = hasVariants
    ? data?.product?.variants?.options.filter(
        option => option.values.length > 0
      )
    : [];
  const needsVariantSelection = neededVariants.length > 0;

  const getVariantTypeMessage = () => {
    if (!needsVariantSelection || !neededVariants.length) return 'variant';
    const neededVariant = neededVariants[0].name;
    return neededVariant.toLowerCase();
  };

  // Get warehouse stock data for the currently selected variant or product
  const getWarehouseStockData = () => {
    if (!data?.product) return [];

    // Product with no variants
    if (data.product.warehouseStock?.length > 0) {
      return data.product.warehouseStock;
    }

    // Product with variants
    if (!hasVariants || !data.product.variants?.values?.length) return [];
    if (needsVariantSelection && !initialIndex) return [];

    const variantArticleNumber = initialIndex || selectedVariant;
    if (!variantArticleNumber) return [];

    const variantData = data.product.variants.values.find(
      variant => variant.articleNumber === variantArticleNumber
    );

    return variantData?.warehouseStock || [];
  };

  // Trigger warehouse stock query
  useEffect(() => {
    if (product?.articleNumber) {
      getWarehouseStock();
    }

    if (initialIndex) {
      setSelectedVariant(initialIndex);
      getWarehouseStock({
        variables: {
          articleNumber: initialIndex
        }
      });
    }
  }, [initialIndex, getWarehouseStock, product?.articleNumber]);

  const availableStores = getAllStores();

  const getAvailableStoresCount = () => {
    const warehouseStock = getWarehouseStockData();
    const storesWithStock = warehouseStock.filter(
      stock => stock.stockLevel > 0
    );
    return storesWithStock.length;
  };

  const getMessage = () => {
    if (loading || storesLoading) {
      return 'Hämtar information...';
    }

    if (needsVariantSelection && !initialIndex) {
      const variantType = getVariantTypeMessage();
      return t('Select {variantType} to see stock status in store', {
        variantType
      });
    }

    const storeCount = getAvailableStoresCount();
    const totalStores = availableStores.length;
    if (storeCount > 0) {
      return t('Available in {storeCount} of {totalStores} stores', {
        storeCount,
        totalStores
      });
    } else {
      return t('Not available in any of {totalStores} stores', {
        totalStores
      });
    }
  };

  const canShowStoreList = () => {
    if (loading || storesLoading) return false;
    if (needsVariantSelection && !initialIndex) return false;
    return getAvailableStoresCount() > 0;
  };

  const getFavoriteStoreStockStatus = () => {
    if (!favoriteStore || !data?.product) {
      return null;
    }

    if (!needsVariantSelection || !hasVariants) {
      const warehouseData = getWarehouseStockData();
      if (!warehouseData || warehouseData.length === 0) {
        return null;
      }

      const store = warehouseData.find(
        store => store.location && store.location.name === favoriteStore
      );

      return store?.stockLevel > 0;
    }

    if (initialIndex || selectedVariant) {
      const warehouseData = getWarehouseStockData();
      if (!warehouseData || warehouseData.length === 0) {
        return null;
      }

      const store = warehouseData.find(
        store => store.location && store.location.name === favoriteStore
      );

      return store?.stockLevel > 0;
    }

    return null;
  };

  return (
    <>
      <StoreStockWrapper>
        <h3>
          <StoreFinderIcon />
          <span>{t('Stockstatus in store')}</span>
        </h3>
        <StockInfo>
          {getMessage()}
          {canShowStoreList() && (
            <DrawerTrigger id="product-store-stock-drawer">
              {({ showTarget, hideTarget, isOpen }) => (
                <ShowStoresButton onClick={isOpen ? hideTarget : showTarget}>
                  {isOpen ? 'Dölj butiker' : 'Visa butiker'}
                </ShowStoresButton>
              )}
            </DrawerTrigger>
          )}
        </StockInfo>

        <FavoriteStoreInfo>
          {t('Favorite store')}:{' '}
          {favoriteStore ? (
            <>
              {favoriteStore}
              {(initialIndex || selectedVariant) &&
                getFavoriteStoreStockStatus() !== null && (
                  <span
                    className={
                      getFavoriteStoreStockStatus()
                        ? 'in-stock'
                        : 'not-in-stock'
                    }
                  >
                    (
                    {getFavoriteStoreStockStatus()
                      ? t('In stock')
                      : t('Out of stock')}
                    )
                  </span>
                )}
              {(needsVariantSelection && !initialIndex) ||
              !needsVariantSelection ? (
                <DrawerTrigger id="product-store-stock-drawer">
                  {({ showTarget }) => (
                    <ChangeStoreButton onClick={showTarget}>
                      {t('Change')}
                    </ChangeStoreButton>
                  )}
                </DrawerTrigger>
              ) : null}
            </>
          ) : (
            <DrawerTrigger id="product-store-stock-drawer">
              {({ showTarget }) => (
                <button
                  onClick={showTarget}
                  style={{
                    background: 'transparent',
                    textDecoration: 'underline'
                  }}
                >
                  {t('Select favorite store')}
                </button>
              )}
            </DrawerTrigger>
          )}
        </FavoriteStoreInfo>
      </StoreStockWrapper>

      <DrawerWrapper>
        <DrawerTarget id="product-store-stock-drawer">
          {drawer => (
            <Drawer isOpen={drawer.isOpen} right backdrop>
              <StoreStockDrawer
                isOpen={drawer.isOpen}
                hideTarget={drawer.hideTarget}
                warehouseStock={getWarehouseStockData()}
                favoriteStore={favoriteStore}
                onSelectFavorite={setFavoriteStore}
                needsVariantSelection={needsVariantSelection}
                hasSelectedVariant={Boolean(initialIndex || selectedVariant)}
                allStores={availableStores}
              />
            </Drawer>
          )}
        </DrawerTarget>
      </DrawerWrapper>
    </>
  );
};
